import { Filter, filterData as fallbackFilters } from "@features/Filter";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const DEBOUNCE_INPUT_MS = 350;
const DEBOUNCE_RANGE_MS = 400;
const TYPES_WITH_DEBOUNCE = new Set(["input", "range"]);

const FilterWrapper = ({ filtersLoading }) => {
  const [values, setValues] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceRef = useRef({}); // per-id таймеры

  // карта типов: id -> type (только из реальных фильтров)
  const typeById = useMemo(() => {
    const list =
      Array.isArray(filtersLoading) && filtersLoading.length > 0
        ? filtersLoading
        : Array.isArray(fallbackFilters)
        ? fallbackFilters
        : [];
    const map = {};
    for (const f of list) if (f?.id) map[f.id] = f.type;
    return map;
  }, [filtersLoading]);

  const serializeToParams = (params, id, type, value) => {
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    const isEmpty = value === "" || value == null || isEmptyArray;

    if (isEmpty) {
      if (type === "range") {
        params.delete(`${id}_from`);
        params.delete(`${id}_to`);
      } else {
        params.delete(id);
      }
      return;
    }

    if (type === "range" && typeof value === "object" && !Array.isArray(value)) {
      value.from ? params.set(`${id}_from`, value.from) : params.delete(`${id}_from`);
      value.to ? params.set(`${id}_to`, value.to) : params.delete(`${id}_to`);
    } else if (type === "select" && Array.isArray(value)) {
      params.set(id, value.map((v) => v.value ?? v).join(","));
    } else if (type === "checkbox") {
      params.set(id, value ? "true" : "false");
    } else {
      params.set(id, value);
    }
  };

  const handleChange = (id, value) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    const type = typeById[id] || "input";

    const runner = () => {
      const params = new URLSearchParams(searchParams);
      serializeToParams(params, id, type, value);
      // при изменении любого фильтра уходим на первую страницу
      params.set("page", "1");
      setSearchParams(params);
    };

    if (TYPES_WITH_DEBOUNCE.has(type)) {
      clearTimeout(debounceRef.current[id]);
      const delay = type === "range" ? DEBOUNCE_RANGE_MS : DEBOUNCE_INPUT_MS;
      debounceRef.current[id] = setTimeout(runner, delay);
    } else {
      runner();
    }
  };

  useEffect(() => {
    return () => {
      Object.values(debounceRef.current || {}).forEach((t) => clearTimeout(t));
    };
  }, []);

  // ⬇️ Не перетираем поля, по которым идёт debounce (во время набора)
  const isPending = (id) => Boolean(debounceRef.current?.[id]);

  // восстановление значений из URL (только для реальных фильтров)
  useEffect(() => {
    const initialValues = {};

    for (const [key, val] of Array.from(searchParams.entries())) {
      // диапазоны только для range и только если нет активного ввода
      if (/_from$|_to$/.test(key)) {
        const [id, sub] = key.split("_");
        if (typeById[id] === "range" && !isPending(id)) {
          initialValues[id] = { ...(initialValues[id] || {}), [sub]: val };
        }
        continue;
      }

      const type = typeById[key];
      if (!type) continue;           // игнорируем page, limit, q и т.п.
      if (isPending(key)) continue;  // не перезаписываем активное поле

      if (type === "select") {
        initialValues[key] = val
          .split(",")
          .filter(Boolean)
          .map((v) => ({ value: v, label: v }));
      } else if (type === "checkbox") {
        initialValues[key] = val === "true";
      } else {
        initialValues[key] = val;
      }
    }

    // чекбоксы по умолчанию false
    Object.entries(typeById).forEach(([id, t]) => {
      if (t === "checkbox" && typeof initialValues[id] === "undefined" && !isPending(id)) {
        initialValues[id] = false;
      }
    });

    if (Object.keys(initialValues).length) {
      setValues((prev) => ({ ...prev, ...initialValues }));
    }
  }, [searchParams, typeById]);

  // АТОМАРНЫЙ СБРОС
  const handleResetAll = useCallback(() => {
    Object.values(debounceRef.current || {}).forEach((t) => clearTimeout(t));
    debounceRef.current = {};

    const allIds = new Set([...Object.keys(typeById), ...Object.keys(values)]);
    const nextValues = {};
    allIds.forEach((id) => {
      const t = typeById[id] || "input";
      if (t === "range") nextValues[id] = { from: "", to: "" };
      else if (t === "select") nextValues[id] = [];
      else if (t === "checkbox") nextValues[id] = false;
      else nextValues[id] = "";
    });
    setValues(nextValues);

    const params = new URLSearchParams(searchParams);
    const keepLimit = params.get("limit") || "100";
    const next = new URLSearchParams();
    next.set("page", "1");
    next.set("limit", keepLimit);
    setSearchParams(next, { replace: true });

    setResetKey((k) => k + 1);
    // если без reload не очищается поиск в вашем проекте — оставляем:
    setTimeout(() => window.location.reload(), 0);
  }, [searchParams, setSearchParams, typeById, values]);

  return (
    <div className="filter-wrapper">
      <Filter
        key={resetKey}
        resetKey={resetKey}
        filters={fallbackFilters}
        filtersLoading={filtersLoading}
        values={values}
        onChange={handleChange}
        onReset={handleResetAll}
      />
    </div>
  );
};

export default FilterWrapper;
